import React, { createContext, useReducer } from "react";

type ErrorContextActionType = "clear" | "filters" | "search";

interface IErrorReadContext {
  somethingWentWrong: boolean;
  message?: string;
}

interface IErrorWriteAction {
  type: ErrorContextActionType;
  message?: string;
}

export interface IErrorContext {
  state: IErrorReadContext;
  dispatch: (value: IErrorWriteAction, delay?: number) => void;
}

export const ErrorContext = createContext<IErrorContext | null>(null);

export const ErrorProvider: React.FunctionComponent<{
  children: React.ReactElement;
}> = ({ children }) => {
  const [state, dispatch] = useReducer(updateState, {
    somethingWentWrong: false,
  });

  function updateState(
    state: IErrorReadContext,
    action: IErrorWriteAction
  ): IErrorReadContext {
    const somethingWentWrong =
      action.type === "filters" || action.type === "search";
    return { somethingWentWrong, message: action.message };
  }

  /**
   * This proxy wraps the dispatch function and provides a short delay before dispatching error states.
   * Delaying the dispatch of an error state gives the UI a little time to display loaders, or skeletons,
   * for actions like 'retry' that might be performed after an error is dispatched and displayed to the user.
   * If we don't delay, an error state might be immediately set on retry, and consequently, any displayed error
   * messages will remain visible, not giving a user visual feedback that a retry is being attempted.
   * @param value The write error action.
   */
  function dispatchProxy(value: IErrorWriteAction, delay?: number) {
    if (value.type === "clear") {
      dispatch(value);
    } else {
      setTimeout(
        () => {
          dispatch(value);
        },
        delay && delay >= 0 ? delay : 1250
      );
    }
  }

  return (
    <ErrorContext.Provider value={{ state, dispatch: dispatchProxy }}>
      {children}
    </ErrorContext.Provider>
  );
};
